import React from 'react';
import { providerOptions } from 'src/pages/AccountConnections/providerOptions';

interface CallErrorDisplayProps {
  errors: string[];
  showWarning?: boolean;
  className?: string;
}

export const CallErrorDisplay: React.FC<CallErrorDisplayProps> = ({
  errors,
  showWarning = true,
  className = '',
}) => {
  if (!errors || errors.length === 0) {
    return null;
  }

  const moreThanOneError = errors.length > 1;

  const errorList = errors.map((error, index) => (
    <div key={index}>
      {moreThanOneError && <strong>{index + 1}.</strong>}
      {parseError(error)}
    </div>
  ));

  if (showWarning) {
    return (
      <div className={className}>
        <strong>{moreThanOneError ? 'Warnings:' : 'Warning:'}</strong>
        <br />
        <br />
        {errorList}
      </div>
    );
  }

  return <div className={className}>{errorList}</div>;
};

const parseError = (error: string): React.ReactNode => {
  // Generic regex patterns for different service types
  const ttsServiceRegex = /.*\s(.+)TTSService.*:(.*)/i;
  const sttServiceRegex = /.*\s(.+)STTService.*:(.*)/i;
  const llmServiceRegex = /.*\s(.+)LLMService.*:(.*)/i;
  const httpRegex = /https?:\/\/[^\s]+/i;

  // Try to match TTS service errors
  const ttsMatch = error.match(ttsServiceRegex);
  if (ttsMatch) {
    const providerName = cleanProviderName(ttsMatch[1]);
    const errorMessage = ttsMatch[2]?.trim();

    return errorMessage ? (
      <>
        <strong>{providerName}:</strong> {errorMessage}
      </>
    ) : (
      <>
        Error in <strong>{providerName}</strong> TTS service
      </>
    );
  }

  // Try to match STT service errors
  const sttMatch = error.match(sttServiceRegex);
  if (sttMatch) {
    const providerName = cleanProviderName(sttMatch[1]);
    const errorMessage = sttMatch[2]?.trim();

    return errorMessage ? (
      <>
        <strong>{providerName}:</strong> {errorMessage}
      </>
    ) : (
      <>
        Error in <strong>{providerName}</strong> STT service
      </>
    );
  }

  // Try to match LLM service errors
  const llmMatch = error.match(llmServiceRegex);
  if (llmMatch) {
    const providerName = cleanProviderName(llmMatch[1]);
    const errorMessage = llmMatch[2]?.trim();

    return errorMessage ? (
      <>
        <strong>{providerName}:</strong> {errorMessage}
      </>
    ) : (
      <>
        Error in <strong>{providerName}</strong> LLM service
      </>
    );
  }

  // Handle HTTP errors
  if (httpRegex.test(error)) {
    // Match ": 404," or ": 404 " (i.e., a colon followed by a 3-digit code and then comma or space)
    const statusCodeMatch = error.match(/:\s*(\d{3})(?:,|\s)/);
    const messageMatch = error.match(/message='([^']+)'/i);

    const statusCode = statusCodeMatch?.[1]; // use [1] to get the captured group
    const message = messageMatch?.[1];

    if (statusCode || message) {
      return (
        <>
          <strong>HTTP:</strong> {statusCode ? statusCode + ': ' : ''}
          {message ?? 'Unknown HTTP error'}
        </>
      );
    }

    return (
      <>
        <strong>HTTP:</strong> Request failed
      </>
    );
  }

  // Fallback: check for known provider names in the error
  for (const provider of providerOptions) {
    if (error.toLowerCase().includes(provider.label.toLowerCase())) {
      return (
        <>
          There were some errors with this call. Please check your{' '}
          <b>{provider.label}</b> integration
        </>
      );
    }
  }

  return (
    <>There was an error with one of the providers used during the call.</>
  );
};

// Helper function to clean provider names (remove "Fluents" prefix if present)
const cleanProviderName = (providerName: string): string => {
  return providerName.replace(/^Fluents/i, '').trim();
};
